import { useMemo } from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { getTransWithParams } from 'modules/i18n';
import { signalMono, signalTextField } from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import { readTextAreaAnswer, readTextAreaConfig } from './textArea';
import { BlockRendererProps, questionIdOf } from './types';

/** Show the hard-limit counter only once it starts to matter, not from the first keystroke. */
const COUNTER_VISIBLE_FROM = 0.8;

/**
 * `text_area@1`: free-form prose from the respondent.
 *
 * The interesting behaviour is the distinction between the two limits, which is the reason this type
 * has both. The **soft budget** is advice — it is announced politely, it can be exceeded, and going
 * over it changes nothing about whether the answer can be submitted. The **hard maximum** is the
 * server's rule, so the field enforces it with `maxLength` and surfaces a remaining-characters
 * counter as it approaches. Conflating them would turn guidance into a wall the respondent hits
 * mid-sentence.
 *
 * The helper line is a live region: a counter that changes silently is invisible to anyone using a
 * screen reader, and it is the only feedback the field gives.
 */
export const TextAreaBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const config = useMemo(() => readTextAreaConfig(block), [block]);
  const text = readTextAreaAnswer(answer)?.text ?? '';

  const questionId = questionIdOf(block);

  /**
   * One line of helper text, three things that might want it. Precedence is deliberate: approaching
   * the hard maximum is the most urgent, then having passed the soft budget, then the budget itself
   * as plain guidance. A non-breaking space keeps the field from shifting when there is nothing to
   * say.
   */
  const helperTextOf = (): string => {
    if (text.length >= config.maxLength * COUNTER_VISIBLE_FROM) {
      return getTransWithParams('guestForm.text.remaining', {
        remaining: config.maxLength - text.length
      });
    }
    if (config.softBudget === null) {
      // A real non-breaking space, which is what the reserved line needs to actually occupy: a plain
      // space collapses and the helper text loses its height, shifting the field as the counter
      // appears and disappears.
      return '\u00a0';
    }
    return text.length > config.softBudget
      ? getTransWithParams('guestForm.text.overBudget', {
          budget: config.softBudget
        })
      : getTransWithParams('guestForm.text.budget', {
          budget: config.softBudget
        });
  };

  return (
    <Stack spacing={3}>
      {/* No derived hint: the budget and the remaining count are the field's own helper line, where
          they change as the answer does. A static copy in the head would go stale mid-sentence. */}
      <BlockHeading block={block} locale={locale} />

      <TextField
        value={text}
        onChange={event => onChange({ text: event.target.value })}
        aria-labelledby={questionId}
        aria-required={block.required}
        inputProps={{
          maxLength: config.maxLength,
          'data-testid': `form-text-${block.instanceId}`
        }}
        // Announce the counter as it changes; silent feedback is no feedback for a screen reader.
        // Tabular numerals keep a shrinking number from shifting the line it sits on.
        FormHelperTextProps={{ 'aria-live': 'polite', sx: signalMono }}
        helperText={helperTextOf()}
        sx={signalTextField}
        fullWidth
        multiline
        minRows={config.rows}
      />
    </Stack>
  );
};
